import {
  type AfterViewInit,
  ChangeDetectorRef,
  Component,
  type ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { decode } from 'blurhash';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import type { UserPreferences } from '../../models/preferences.interface';
import { EStudyStatus } from '../../models/study.interface';
import { PreferencesService } from '../../services/preferences.service';

@Component({
  selector: 'app-preview-card',
  templateUrl: './preview-card.component.html',
  styleUrls: ['./preview-card.component.scss']
})
export class PreviewCardComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() hash?: string | undefined;
  @Input() previewPublicId?: string | undefined;
  @Input() directUrl?: string | undefined;
  @Input() status?: EStudyStatus | undefined;
  @Input() modalityTypeName?: string | undefined;
  @Input() bigPreview = false;
  @Input() showTransferFailedAlert = false;
  // True when the local clinic Orthanc is reachable: the study data is already
  // on-site and is being pushed to the cloud → show "Transfert vers le cloud…".
  @Input() availableLocally = false;

  @ViewChild('canvas') readonly canvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('img') readonly img: ElementRef<HTMLImageElement>;

  resolved = false;
  isLoading = true;
  isUploading = false;
  isTransferFailed = false;
  shouldLazyLoad = false;
  placeholderUrl = '';
  url = '';

  private readonly subscription = new Subscription();

  // Translation key for the "transfer in progress" label: on the clinic network the data
  // is already on-site and being pushed to the cloud, so we say so explicitly.
  get uploadingMessageKey(): string {
    return this.availableLocally ? 'studies.cards.uploading_to_cloud' : 'studies.cards.uploading';
  }

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly preferencesService: PreferencesService
  ) {}

  ngAfterViewInit(): void {
    this.setupView();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['hash']?.previousValue !== changes['hash']?.currentValue ||
      changes['status']?.previousValue !== changes['status']?.currentValue ||
      changes['previewPublicId']?.previousValue !== changes['previewPublicId']?.currentValue
    ) {
      this.setupView();
    }

    // Détecter les changements de showTransferFailedAlert
    if (changes['showTransferFailedAlert']) {
      this.updateTransferFailedState();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private updateTransferFailedState(): void {
    this.isUploading = (this.status && [EStudyStatus.New, EStudyStatus.Uploading].includes(this.status)) ?? false;
    this.isTransferFailed = this.showTransferFailedAlert && this.isUploading;
  }

  private setupView() {
    // this.resolved = false;
    this.isLoading = true;

    this.createImgInstance();
    this.drawCanvas();
  }

  private createImgInstance(): void {
    this.subscription.add(this.preferencesService.getPreferences().subscribe(pref => {
      this.setEmptyState(pref);

      this.isUploading = (this.status && [EStudyStatus.New, EStudyStatus.Uploading].includes(this.status)) ?? false;
      this.isTransferFailed = this.showTransferFailedAlert && this.isUploading;

      if (this.previewPublicId) {
        this.url = `${environment.apiUrl}/instance/public${this.bigPreview ? '' : '-preview'}/${this.previewPublicId}.png`;
        this.shouldLazyLoad = true;
      } else if (this.directUrl) {
        this.url = this.directUrl;
        this.shouldLazyLoad = true;
      } else if (this.isUploading) {
        if (this.hash) {
          this.url = '';
          this.shouldLazyLoad = false;
        }
      }

      // For lazy loading, we'll let the directive handle the image loading
      if (this.shouldLazyLoad) {
        this.resolved = true;
        this.isLoading = true;
        this.changeDetectorRef.detectChanges();
      } else {
        // Original image loading logic for non-lazy loaded images
        const img = new Image();
        img.src = this.url;

        img.onload = () => {
          if (img.naturalWidth <= 0 && img.naturalHeight <= 0) {
            this.setEmptyState(pref);
          }

          this.onLoadImg();
        };

        img.onerror = () => {
          if (this.isUploading) {
            return;
          }

          if (this.hash) {
            this.isLoading = false;
          } else {
            this.setEmptyState(pref);
            this.onLoadImg();
          }
        };
      }
    })
    );
  }

  private setEmptyState(pref: UserPreferences): void {
    const isDarkMode = pref.preferenciesObject.isDarkMode;

    this.placeholderUrl = `/assets/img/empty-states/empty_other${isDarkMode ? '_dark' : ''}.png`;

    if (this.modalityTypeName === 'SR') {
      this.placeholderUrl = `/assets/img/empty-states/empty_doc${isDarkMode ? '_dark' : ''}.png`;
    }

    // If no real URL, use placeholder as main URL
    if (!this.url) {
      this.url = this.placeholderUrl;
    }
  }

  private onLoadImg(): void {
    this.resolved = true;
    this.isLoading = true;
    this.changeDetectorRef.detectChanges();
    if (this.img) {
      this.img.nativeElement.src = this.url;
    }
  }

  private drawCanvas(): void {
    if (!this.hash || !this.canvas) {
      return;
    }

    const pixels = decode(this.hash, 350, 200);
    const context = this.canvas.nativeElement.getContext('2d');
    if (context) {
      const imageData = context.createImageData(350, 200);
      imageData.data.set(pixels);
      context.putImageData(imageData, 0, 0);
    }
  }
}
